import React, { useCallback, useEffect, useMemo, useState } from 'react';

const CATEGORY_OPTIONS = [
  // Income
  'Registration',
  'Donations',
  'Concessions (Dining Hall)',
  'Concessions (Sideboard)',
  'Other Income',

  // Expenses
  'Staff',
  'Utilities',
  'Supplies',
  'Food',
  'Marketing',
  'Scholarships',
  'Other Expense'
];

const METHOD_OPTIONS = ['Cash', 'Check', 'PayPal', 'Venmo', 'Zelle', 'Card', 'Youth Group', 'Other'];
const INCOME_CATEGORIES = new Set(['Registration', 'Donations', 'Concessions (Dining Hall)', 'Concessions (Sideboard)', 'Other Income']);
const EXPENSE_CATEGORIES = new Set(['Staff', 'Utilities', 'Supplies', 'Food', 'Marketing', 'Scholarships', 'Other Expense']);

export default function AdminFinancials({
  token,
  financeData,
  financeLoading,
  financeError,

  finDate,
  setFinDate,
  finCategory,
  setFinCategory,
  finDescription,
  setFinDescription,
  finAmount,
  setFinAmount,
  finMethod,
  setFinMethod,
  finNotes,
  setFinNotes,
  fetchFinance,
  addFinance,

  // when true, hides all write actions (import/add/edit/delete)
  readOnly = false
}) {
  const rows = useMemo(() => {
    const r = financeData?.rows;
    return Array.isArray(r) ? r : [];
  }, [financeData]);

  const toNumber = (v) => {
    const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const fmtUsd = (n) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(toNumber(n));

  const isIncome = (r) => String(r?.type || '').toLowerCase() === 'income';
  const isExpense = (r) => String(r?.type || '').toLowerCase() === 'expense';

  // Display helpers
  const showDate = (r) => String(r?.date || r?.Date || r?.createdAt || r?.created_at || '').trim();
  const showRawCategory = useCallback((r) => String(r?.category || r?.Category || '').trim(), []);
  const showCategory = useCallback((r) => {
    const category = showRawCategory(r);
    if (/^registration\s*\(received\)$/i.test(category)) return 'Registration';
    return category;
  }, [showRawCategory]);
  const showDesc = (r) => String(r?.description || r?.Description || '').trim();
  const showAmount = (r) => String(r?.amount || r?.Amount || '').trim();
  const showPaidBy = (r) => String(r?.paidBy || r?.PaidBy || r?.paid_by || '').trim();
  const showMethod = (r) => String(r?.method || r?.Method || '').trim();
  const showNotes = (r) => String(r?.notes || r?.Notes || '').trim();

  const isExpectedRegistrationRow = useCallback((r) => /^registration\s*\(expected\)$/i.test(showRawCategory(r)), [showRawCategory]);

  // P&L rules: only count actual received income + real expenses
  const isPnlIncomeRow = (r) => isIncome(r) && INCOME_CATEGORIES.has(showCategory(r));
  const isPnlExpenseRow = (r) => isExpense(r) && EXPENSE_CATEGORIES.has(showCategory(r));

  const totalIncome = rows.filter(isPnlIncomeRow).reduce((sum, r) => sum + toNumber(r.amount), 0);
  const totalExpense = rows.filter(isPnlExpenseRow).reduce((sum, r) => sum + toNumber(r.amount), 0);
  const net = totalIncome - totalExpense;

  // Add-entry validation
  const validateAdd = () => {
    if (!String(finDate || '').trim()) return 'Please select a date.';
    if (!String(finCategory || '').trim()) return 'Please select a category.';
    if (!String(finAmount || '').trim()) return 'Please enter an amount.';
    const n = Number(String(finAmount).replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(n)) return 'Amount must be a number.';
    return '';
  };

  // Search + pagination
  const [query, setQuery] = useState('');
  const [pageSize, setPageSize] = useState(25);

  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [editId, setEditId] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editType, setEditType] = useState('income');
  const [editCategory, setEditCategory] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editPaidBy, setEditPaidBy] = useState('');
  const [editMethod, setEditMethod] = useState('');
  const [editNotes, setEditNotes] = useState('');

  // Collapsed state per category group
  const [collapsed, setCollapsed] = useState({});
  const toggleCollapsed = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  // page per group key
  const [groupPages, setGroupPages] = useState({});

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  const totalPages = (arrLen) => Math.max(1, Math.ceil(arrLen / Math.max(1, pageSize)));

  const slicePage = (arr, page) => {
    const size = Math.max(1, pageSize);
    const pages = totalPages(arr.length);
    const p = clamp(page, 1, pages);
    const start = (p - 1) * size;
    return arr.slice(start, start + size);
  };

  useEffect(() => {
    setGroupPages({});
    setCollapsed({});
  }, [rows.length]);

  const filteredRows = useMemo(() => {
    const q = String(query || '').toLowerCase().trim();
    if (!q) return rows.filter((r) => !isExpectedRegistrationRow(r));

    return rows.filter((r) => {
      if (isExpectedRegistrationRow(r)) return false;
      const hay = [
        showCategory(r),
        showDesc(r),
        showDate(r),
        showAmount(r),
        showPaidBy(r),
        showMethod(r),
        showNotes(r),
        String(r?.type || '')
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return hay.includes(q);
    });
  }, [rows, query, isExpectedRegistrationRow, showCategory]);

  const incomeRows = useMemo(() => filteredRows.filter(isIncome), [filteredRows]);
  const expenseRows = useMemo(() => filteredRows.filter(isExpense), [filteredRows]);

  const incomeGroups = useMemo(() => {
    const map = new Map();
    incomeRows.forEach((r) => {
      const key = showCategory(r) || 'Uncategorized Income';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });

    const groups = Array.from(map.entries()).map(([category, items]) => ({
      category,
      items,
      total: items.reduce((sum, r) => sum + toNumber(r.amount), 0)
    }));

    groups.sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
    return groups;
  }, [incomeRows, showCategory]);

  const expenseGroups = useMemo(() => {
    const map = new Map();
    expenseRows.forEach((r) => {
      const key = showCategory(r) || 'Uncategorized Expenses';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });

    const groups = Array.from(map.entries()).map(([category, items]) => ({
      category,
      items,
      total: items.reduce((sum, r) => sum + toNumber(r.amount), 0)
    }));

    groups.sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
    return groups;
  }, [expenseRows, showCategory]);

  const incomeByMethod = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      if (!isIncome(r)) return;
      if (!INCOME_CATEGORIES.has(showCategory(r))) return;
      const method = showMethod(r) || 'Unspecified';
      m.set(method, (m.get(method) || 0) + toNumber(r.amount));
    });

    return Array.from(m.entries())
      .map(([method, total]) => ({ method, total }))
      .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  }, [rows, showCategory]);

  const deleteFinanceEntry = async (row) => {
    if (readOnly) return;

    const id = String(row?.id || row?.ID || '').trim();
    if (!id) return;

    const ok = window.confirm('Delete this financial entry? This cannot be undone.');
    if (!ok) return;

    const authToken = token || localStorage.getItem('adminToken') || '';

    const res = await fetch('/.netlify/functions/admin-delete-finance', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ id })
    });

    const raw = await res.text();
    let json = {};
    try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }

    if (!res.ok) {
      alert(json?.error || 'Failed to delete entry');
      return;
    }

    await fetchFinance(authToken);
  };

  const openEdit = (row) => {
    if (readOnly) return;

    const id = String(row?.id || row?.ID || '').trim();
    if (!id) {
      alert('Missing id for this row.');
      return;
    }

    setEditError('');
    setEditId(id);
    setEditDate(showDate(row) || '');
    setEditType(String(row?.type || '').toLowerCase() || 'income');
    setEditCategory(showCategory(row) || '');
    setEditDescription(showDesc(row) || '');
    setEditAmount(String(row?.amount ?? row?.Amount ?? '').trim());
    setEditPaidBy(showPaidBy(row) || '');
    setEditMethod(showMethod(row) || '');
    setEditNotes(showNotes(row) || '');

    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (readOnly) return;

    setEditError('');

    if (!editId) return setEditError('Missing id.');
    if (!editDate) return setEditError('Date is required.');
    if (!editType) return setEditError('Type is required.');
    if (!editCategory) return setEditError('Category is required.');
    if (editAmount === '' || !Number.isFinite(Number(editAmount))) return setEditError('Amount must be a number.');

    const authToken = token || localStorage.getItem('adminToken') || '';

    setEditSaving(true);
    try {
      const payload = {
        id: editId,
        date: editDate,
        type: editType,
        category: editCategory,
        description: editDescription,
        amount: editAmount,
        paidBy: editPaidBy,
        method: editMethod,
        notes: editNotes
      };

      const res = await fetch('/.netlify/functions/admin-update-finance', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }

      if (!res.ok) {
        setEditError(json?.error || `Failed to update (status ${res.status})`);
        return;
      }

      setEditOpen(false);
      await fetchFinance(authToken);
    } catch (e) {
      setEditError('Network error while saving.');
    } finally {
      setEditSaving(false);
    }
  };

  const renderEntryCard = (r, idx) => {
    const amt = showAmount(r);
    const cat = showCategory(r);
    const dt = showDate(r);
    const desc = showDesc(r);
    const paidBy = showPaidBy(r);
    const method = showMethod(r);
    const notes = showNotes(r);
    const type = String(r?.type || '').toLowerCase();
    const typeLabel = type === 'income' ? 'Income' : type === 'expense' ? 'Expense' : (r?.type || '');
    const id = String(r?.id || r?.ID || '').trim();

    return (
      <div
        key={String(r?.id || r?.ID || `${dt}-${cat}-${amt}-${idx}`)}
        style={{
          border: '1px solid #eee',
          borderRadius: 10,
          padding: 12,
          background: '#fff',
          minWidth: 0
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, minWidth: 0, wordBreak: 'break-word' }}>
            {cat || '(no category)'}
          </div>
          <div style={{ fontWeight: 800, color: type === 'expense' ? '#b42318' : '#067647' }}>
            {fmtUsd(amt)}
          </div>
        </div>

        <div className="muted" style={{ marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span>{dt ? dt : ''}</span>
          <span>{typeLabel ? `• ${typeLabel}` : ''}</span>
        </div>

        {desc ? (
          <div className="muted" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
            {desc}
          </div>
        ) : null}

        {(paidBy || method || notes) ? (
          <div className="muted" style={{ marginTop: 8, fontSize: 13, whiteSpace: 'pre-wrap' }}>
            {paidBy ? `Paid by: ${paidBy}` : ''}
            {paidBy && method ? ' • ' : ''}
            {method ? `Method: ${method}` : ''}
            {notes ? `\n${notes}` : ''}
          </div>
        ) : null}

        {!readOnly ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button className="btn" type="button" onClick={() => openEdit(r)} disabled={!id}>
              Edit
            </button>
            <button className="btn" type="button" onClick={() => deleteFinanceEntry(r)} disabled={!id}>
              Delete
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const Pager = ({ page, setPage, pages }) => (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
      <button className="btn" type="button" onClick={() => setPage(1)} disabled={page <= 1}>
        First
      </button>
      <button className="btn" type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
        Prev
      </button>
      <div className="muted" style={{ fontSize: 13 }}>
        Page {page} of {pages}
      </div>
      <button
        className="btn"
        type="button"
        onClick={() => setPage((p) => Math.min(pages, p + 1))}
        disabled={page >= pages}
      >
        Next
      </button>
      <button className="btn" type="button" onClick={() => setPage(pages)} disabled={page >= pages}>
        Last
      </button>
    </div>
  );

  const CategoryGroup = ({ type, group }) => {
    const key = `${type}::${group.category}`;
    const isClosed = Object.prototype.hasOwnProperty.call(collapsed, key) ? !!collapsed[key] : false;

    const pages = totalPages(group.items.length);
    const currentPage = Object.prototype.hasOwnProperty.call(groupPages, key) ? groupPages[key] : 1;
    const safePage = clamp(currentPage, 1, pages);

    useEffect(() => {
      if (safePage !== currentPage) {
        setGroupPages((prev) => ({ ...prev, [key]: safePage }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pages, pageSize]);

    const pageItems = useMemo(() => slicePage(group.items, safePage), [group.items, safePage]);

    const setPage = (next) => {
      const newPage = typeof next === 'function' ? next(safePage) : next;
      setGroupPages((prev) => ({ ...prev, [key]: newPage }));
    };

    return (
      <div style={{ border: '1px solid #eee', borderRadius: 10, background: '#fff', overflow: 'hidden' }}>
        <button
          type="button"
          onClick={() => toggleCollapsed(key)}
          className="btn"
          style={{
            width: '100%',
            textAlign: 'left',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            padding: '10px 12px',
            borderRadius: 0,
            background: '#fff'
          }}
        >
          <div style={{ fontWeight: 800 }}>
            {group.category}{' '}
            <span className="muted" style={{ fontWeight: 600 }}>
              ({group.items.length})
            </span>
          </div>

          <div style={{ fontWeight: 900, color: type === 'expense' ? '#b42318' : '#067647' }}>
            {fmtUsd(group.total)}
            <span className="muted" style={{ fontWeight: 600, marginLeft: 10 }}>
              {isClosed ? '▸' : '▾'}
            </span>
          </div>
        </button>

        {!isClosed ? (
          <div style={{ padding: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {pageItems.map(renderEntryCard)}
            </div>

            {group.items.length > pageSize ? <Pager page={safePage} setPage={setPage} pages={pages} /> : null}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div style={{ marginTop: 14 }}>
      <h2 style={{ margin: '6px 0 10px 0' }}>Financials</h2>

      {/* Summary cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 10,
          marginTop: 10
        }}
      >
        <div style={{ border: '1px solid #eee', borderRadius: 10, padding: 12, background: '#fff', minWidth: 0 }}>
          <div style={{ color: '#666', fontSize: 12 }}>Total Income (Received)</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtUsd(totalIncome)}</div>
        </div>
        <div style={{ border: '1px solid #eee', borderRadius: 10, padding: 12, background: '#fff', minWidth: 0 }}>
          <div style={{ color: '#666', fontSize: 12 }}>Total Expenses</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtUsd(totalExpense)}</div>
        </div>
        <div style={{ border: '1px solid #eee', borderRadius: 10, padding: 12, background: '#fff', minWidth: 0 }}>
          <div style={{ color: '#666', fontSize: 12 }}>Net (Surplus/Deficit)</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: net < 0 ? '#b42318' : '#067647' }}>
            {fmtUsd(net)}
          </div>
        </div>
      </div>

      {/* Income by method */}
      <div style={{ marginTop: 12, border: '1px solid #eee', borderRadius: 10, padding: 12, background: '#fff' }}>
        <div style={{ fontWeight: 800 }}>Income by method</div>
        <div className="muted" style={{ marginTop: 6, fontSize: 13 }}>
          (Counts only actual received income categories)
        </div>

        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          {incomeByMethod.length ? incomeByMethod.map((x) => (
            <div key={x.method} style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: 10 }}>
              <div className="muted" style={{ fontSize: 12 }}>{x.method}</div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{fmtUsd(x.total)}</div>
            </div>
          )) : (
            <div className="muted">No income entries yet.</div>
          )}
        </div>
      </div>

      {financeLoading && <div style={{ marginTop: 10 }}>Loading financials…</div>}
      {financeError ? <div className="error" style={{ marginTop: 10 }}>{financeError}</div> : null}

      {/* Add entry */}
      {!readOnly ? (
        <div style={{ marginTop: 12, border: '1px solid #eee', borderRadius: 10, padding: 12, background: '#fff' }}>
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Date</div>
              <input
                type="date"
                value={finDate}
                onChange={(e) => setFinDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Category</div>
              <select
                value={finCategory}
                onChange={(e) => setFinCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  boxSizing: 'border-box',
                  background: '#fff'
                }}
              >
                <option value="" disabled>Select category…</option>
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Amount</div>
              <input
                type="text"
                value={finAmount}
                onChange={(e) => setFinAmount(e.target.value)}
                placeholder="e.g. 120 or -45"
                inputMode="decimal"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

            <div style={{ marginTop: 10, display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Method</div>
                <select
                value={finMethod || ''}
                onChange={(e) => setFinMethod(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  boxSizing: 'border-box',
                  background: '#fff'
                }}
              >
                <option value="">(optional)</option>
                {METHOD_OPTIONS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
                </select>
              </div>
            </div>

          <div style={{ marginTop: 10, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Description</div>
            <textarea
              value={String(finDescription || '')}
              onChange={(e) => setFinDescription(e.target.value)}
              placeholder="What is this for?"
              rows={2}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #ddd',
                boxSizing: 'border-box',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ marginTop: 10, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Notes (optional)</div>
            <textarea
              value={finNotes || ''}
              onChange={(e) => setFinNotes(e.target.value)}
              placeholder="Room/bed, partial payment details, receipt #, etc."
              rows={2}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #ddd',
                boxSizing: 'border-box',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn primary"
              onClick={() => {
                const msg = validateAdd();
                if (msg) return alert(msg);
                addFinance('income');
              }}
              disabled={financeLoading}
            >
              Add Income
            </button>
            <button
              className="btn"
              onClick={() => {
                const msg = validateAdd();
                if (msg) return alert(msg);
                addFinance('expense');
              }}
              disabled={financeLoading}
            >
              Add Expense
            </button>
          </div>
        </div>
      ) : null}

      {/* Filter bar */}
      <div style={{ marginTop: 14, border: '1px solid #eee', borderRadius: 10, padding: 12, background: '#fff' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ minWidth: 220, flex: 1 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Search</div>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search category, description, date, amount, paid by, method…"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #ddd',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ width: 180 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Rows per page</div>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #ddd',
                boxSizing: 'border-box'
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <button className="btn" type="button" onClick={() => setQuery('')}>
            Clear
          </button>
        </div>

        <div className="muted" style={{ marginTop: 10, fontSize: 13 }}>
          Showing: {filteredRows.length} of {rows.length} entries
        </div>
      </div>

      {/* Grouped lists */}
      <div
        style={{
          marginTop: 14,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 12
        }}
      >
        <div style={{ border: '1px solid #eee', borderRadius: 10, padding: 12, background: '#fff', minWidth: 0 }}>
          <h3 style={{ margin: '4px 0 10px 0' }}>Income ({incomeRows.length})</h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {incomeGroups.map((g) => (
              <CategoryGroup key={`income-${g.category}`} type="income" group={g} />
            ))}
            {!incomeRows.length ? <div className="muted">No income entries.</div> : null}
          </div>
        </div>

        <div style={{ border: '1px solid #eee', borderRadius: 10, padding: 12, background: '#fff', minWidth: 0 }}>
          <h3 style={{ margin: '4px 0 10px 0' }}>Expenses ({expenseRows.length})</h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {expenseGroups.map((g) => (
              <CategoryGroup key={`expense-${g.category}`} type="expense" group={g} />
            ))}
            {!expenseRows.length ? <div className="muted">No expense entries.</div> : null}
          </div>
        </div>
      </div>

      {!readOnly && editOpen ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 12,
            zIndex: 9999
          }}
          onClick={() => (editSaving ? null : setEditOpen(false))}
        >
          <div
            style={{
              width: 'min(760px, 96vw)',
              background: '#fff',
              borderRadius: 12,
              border: '1px solid #eee',
              padding: 14
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Edit entry</h3>
              <button className="btn" type="button" onClick={() => setEditOpen(false)} disabled={editSaving}>
                Close
              </button>
            </div>

            {editError ? <div className="error" style={{ marginTop: 10 }}>{editError}</div> : null}

            <div style={{ marginTop: 12, display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Date</div>
                <input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Type</div>
                <select
                  value={editType}
                  onChange={(e) => setEditType(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                >
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                </select>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Category</div>
                <select
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box', background: '#fff' }}
                >
                  <option value="" disabled>Select category…</option>
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Description</div>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Amount</div>
                <input
                  type="text"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  inputMode="decimal"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Method</div>
                <select
                  value={editMethod || ''}
                  onChange={(e) => setEditMethod(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box', background: '#fff' }}
                >
                  <option value="">(optional)</option>
                  {METHOD_OPTIONS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Paid by</div>
                <input
                  type="text"
                  value={editPaidBy}
                  onChange={(e) => setEditPaidBy(e.target.value)}
                  placeholder="Camper / Sponsor / Youth group name"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Notes</div>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
            </div>

            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn" type="button" onClick={() => setEditOpen(false)} disabled={editSaving}>
                Cancel
              </button>
              <button className="btn primary" type="button" onClick={saveEdit} disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
